import { Button } from "@a2z/ui";
import { keys } from "@/lib/i18n/portal";

import { useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { ImageWithPlaceholder } from "@/shared/components/PlaceholderImage";

type ViewerBgRemoval = {
  originalUrl: string | null;
  cleanUrl: string | null;
};

type Props = {
  activeUrl: string | null;
  viewerIndex: number | null;
  totalItems: number;
  activeBgRemoval?: ViewerBgRemoval | null;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

export function ProductImagesViewer({
  activeUrl,
  viewerIndex,
  totalItems,
  activeBgRemoval,
  onPrev,
  onNext,
  onClose,
}: Props) {
  const t = useTranslations();
  const swipeStartX = useRef<number | null>(null);
  // Local preview-only toggle — does not affect saved state
  const [viewerVersion, setViewerVersion] = useState<"original" | "clean">("clean");

  if (!activeUrl) {
    return null;
  }

  const showVersionToggle =
    activeBgRemoval &&
    activeBgRemoval.originalUrl &&
    activeBgRemoval.cleanUrl;

  const displayUrl = showVersionToggle
    ? viewerVersion === "clean"
      ? activeBgRemoval.cleanUrl
      : activeBgRemoval.originalUrl
    : activeUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 sm:px-6 sm:py-10">
      <div className="flex h-full w-full flex-col gap-4 bg-white p-4 shadow-[var(--shadow-md)] sm:h-[90vh] sm:max-w-5xl sm:rounded-2xl">
        <div className="flex flex-wrap items-center justify-between gap-y-2">
          <div className="text-xs font-semibold text-[color:var(--text-muted)]">
            {viewerIndex !== null ? `${viewerIndex + 1} / ${totalItems}` : ""}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {showVersionToggle ? (
              <div
                className="flex items-center rounded-lg border border-[color:var(--border-light)] p-0.5"
                role="group"
                aria-label={t(keys.products.bgRemovalToggleLabel)}
              >
                <button
                  type="button"
                  onClick={() => setViewerVersion("original")}
                  className={[
                    "rounded-md px-2 py-1 text-xs font-semibold transition",
                    viewerVersion === "original"
                      ? "bg-[color:var(--surface-elevated)] text-[color:var(--text-primary)] shadow-sm"
                      : "text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)]",
                  ].join(" ")}
                  aria-pressed={viewerVersion === "original"}
                >
                  {t(keys.products.backgroundOriginal)}
                </button>
                <button
                  type="button"
                  onClick={() => setViewerVersion("clean")}
                  className={[
                    "rounded-md px-2 py-1 text-xs font-semibold transition",
                    viewerVersion === "clean"
                      ? "bg-[color:var(--accent-500)] text-[color:var(--primary-500)] shadow-sm"
                      : "text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)]",
                  ].join(" ")}
                  aria-pressed={viewerVersion === "clean"}
                >
                  {t(keys.products.backgroundClean)}
                </button>
              </div>
            ) : null}
            <Button
              type="button"
              onClick={onPrev}
              variant="outline"
              className="h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold"
            >
              {t(keys.common.previous)}
            </Button>
            <Button
              type="button"
              onClick={onNext}
              variant="outline"
              className="h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold"
            >
              {t(keys.common.next)}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold"
            >
              {t(keys.common.close)}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t(keys.products.imagesTitle)}
          className="relative flex min-h-0 flex-1 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-white"
          onPointerDown={(event) => {
            swipeStartX.current = event.clientX;
          }}
          onPointerUp={(event) => {
            if (swipeStartX.current === null) {
              return;
            }
            const delta = event.clientX - swipeStartX.current;
            swipeStartX.current = null;
            if (Math.abs(delta) < 40) {
              return;
            }
            if (delta > 0) {
              onPrev();
            } else {
              onNext();
            }
          }}
          style={{ touchAction: "pan-y" }}
        >
          <ImageWithPlaceholder
            src={displayUrl ?? activeUrl}
            alt={t(keys.products.imagesTitle)}
            seed={activeUrl}
            className="max-h-full max-w-full object-contain"
          />
        </button>
      </div>
    </div>
  );
}
